import { ConditionOptions } from "@/utility/conditionalExpressions";
import { Aggregate } from "@mm/api.meteomatics.com";
import type {
  AdvancedThresholdDetailsSchema,
  AdvancedThresholdSchema,
  ColorThreshold,
} from "@mm/metx-workbench.meteomatics.com";
import { v4 } from "uuid";

type AdvancedThresholdDetailsSchemaExperimental = AdvancedThresholdDetailsSchema & {
  calibrated?: boolean;
  api_url_suffix?: string;
};

export type ThresholdDetails = {
  condition: ConditionOptions;
  value: number;
};

export type ThresholdColor = ThresholdDetails & {
  color: string;
};

export const transformColorThresholds = (thresholds?: ColorThreshold[] | null): ThresholdColor[] => {
  if (!thresholds) {
    return [];
  }
  return thresholds.map((threshold) => {
    const isValidCondition = Object.values(ConditionOptions).includes(threshold.condition as ConditionOptions);

    if (!isValidCondition) {
      console.warn(`Invalid condition "${threshold.condition}" found. Defaulting to "==".`);
    }

    return {
      color: threshold.color,
      value: threshold.value,
      condition: isValidCondition ? (threshold.condition as ConditionOptions) : ConditionOptions["=="],
    };
  });
};

export type AdvancedThresholdDetailsCore = {
  parameter: string;
  model: string;
  offsetMinutes: number;
  aggregate?: Aggregate;
  id: string;
  calibrated?: boolean;
  apiUrlSuffix?: string;
};

export type AdvancedThresholdDetails = ThresholdDetails & AdvancedThresholdDetailsCore;

export const getDetailsCore = (details: AdvancedThresholdDetails[]): AdvancedThresholdDetailsCore[] =>
  details.map(({ id, aggregate, offsetMinutes, model, parameter, calibrated, apiUrlSuffix }) => ({
    id,
    aggregate,
    offsetMinutes,
    model,
    parameter,
    calibrated,
    apiUrlSuffix,
  }));
export const getDefaultAdvancedThresholdDetails = (): AdvancedThresholdDetails => ({
  id: v4(),
  model: "mix",
  aggregate: Aggregate.max,
  offsetMinutes: 0,
  value: 0,
  parameter: "",
  condition: ConditionOptions[">="],
  calibrated: false,
});

export type BasicCondition = "and" | "or";

export type AdvancedThreshold = {
  label: string;
  color: string;
  conditions: BasicCondition[];
  details: AdvancedThresholdDetails[];
  id: string;
};

export const transformAdvancedThresholds = (thresholds?: AdvancedThresholdSchema[] | null): AdvancedThreshold[] => {
  if (!thresholds) {
    return [];
  }
  return thresholds.map((threshold, index) => {
    const basicConditions: BasicCondition[] =
      threshold.conditions?.map((condition) => {
        const normalizedCondition = condition.toLowerCase();
        if (normalizedCondition === "and") {
          return "and";
        }
        if (normalizedCondition === "or") {
          return "or";
        }
        console.warn(`Condition ${normalizedCondition} is not valid, falling back to 'and'`);
        return "and";
      }) || [];
    const details: AdvancedThresholdDetails[] =
      threshold.thresholds?.map((detail) => {
        const detailWithExperimentalFields = detail as AdvancedThresholdDetailsSchemaExperimental;
        const isValidCondition = Object.values(ConditionOptions).includes(detail.condition as ConditionOptions);

        if (!isValidCondition) {
          console.warn(`Invalid condition "${detail.condition}" found. Defaulting to "==".`);
        }

        const isValidAggregate = Object.values(Aggregate).includes(detail.aggregate as Aggregate);

        if (!isValidAggregate) {
          console.warn(`Invalid aggregate "${detail.aggregate}" found. Defaulting to "mean".`);
        }

        return {
          id: detail.id || v4(),
          parameter: detail.parameter,
          model: detail.model,
          value: detail.value,
          condition: isValidCondition ? (detail.condition as ConditionOptions) : ConditionOptions["=="],
          offsetMinutes: detail.time_offset,
          aggregate: detail.aggregate
            ? isValidAggregate
              ? (detail.aggregate as Aggregate)
              : Aggregate.mean
            : undefined,
          calibrated: detailWithExperimentalFields.calibrated ?? false,
          apiUrlSuffix: detailWithExperimentalFields.api_url_suffix,
        };
      }) || [];

    return {
      color: threshold.color || "#FF0000",
      label: threshold.title || `Threshold (${index + 1})`,
      conditions: basicConditions,
      details: details,
      id: threshold.id || v4(),
    };
  });
};

export const transformAdvancedThresholdsSchema = (thresholds?: AdvancedThreshold[]): AdvancedThresholdSchema[] => {
  if (!thresholds) {
    return [];
  }
  return thresholds.map((threshold, index) => {
    const details: AdvancedThresholdDetailsSchemaExperimental[] =
      threshold.details?.map((detail) => {
        return {
          id: detail.id || v4(),
          parameter: detail.parameter,
          model: detail.model,
          condition: detail.condition.toLowerCase(),
          value: detail.value,
          time_offset: detail.offsetMinutes,
          aggregate: detail.aggregate?.toLowerCase() || "max",
          calibrated: detail.calibrated ?? false,
          api_url_suffix: detail.apiUrlSuffix || undefined,
        };
      }) || [];

    return {
      id: threshold.id || v4(),
      color: threshold.color || "#FF0000",
      title: threshold.label || `Threshold (${index + 1})`,
      conditions: threshold.conditions.map((condition) => condition.toLowerCase()),
      thresholds: details,
    };
  });
};

export const getDefaultAdvancedThreshold = (index: number): AdvancedThreshold => ({
  label: `Threshold ${index}`,
  color: "red",
  id: v4(),
  conditions: ["and"],
  details: [
    {
      id: v4(),
      model: "mix",
      aggregate: Aggregate.max,
      offsetMinutes: 0,
      value: 25,
      parameter: "t_2m:C",
      condition: ConditionOptions[">="],
    },
    getDefaultAdvancedThresholdDetails(),
  ],
});
